import { missionTle, pointingFor } from '@/features/dive/pointing';
import { parseTLE, getPositionEcf, checkVisibility } from '../engine/orbits';
import type { Antenna, Mission, ContactWindow } from '../types';
import type { AnalysisResults } from '../analysis/types';

export function elevationSamples(mission: Mission, antenna: Antenna, pass: ContactWindow) {
  const tle = missionTle(mission),
    satrec = tle ? parseTLE(tle) : null;
  if (!satrec || pass.endMs <= pass.startMs) return [];
  return Array.from({ length: 121 }, (_, i) => {
    const timeMs = pass.startMs + ((pass.endMs - pass.startMs) * i) / 120;
    const ecf = getPositionEcf(satrec, timeMs);
    return { timeMs, elevation: ecf ? checkVisibility(ecf, antenna.location, 0).elevation : NaN };
  }).filter((p) => Number.isFinite(p.elevation));
}

/** Only name constraints for which we have inputs. Receiver confirmation has no feed. */
export function explainLink(
  mission: Mission,
  antenna: Antenna,
  nowMs: number,
  results: AnalysisResults | null,
): string[] {
  if (antenna.capabilities.monitoringOnly) return ['RFIMS monitoring location: receiver configuration, RF pattern and mission assignment are unverified. Geometric visibility is not a confirmed reception.'];
  const reasons: string[] = [];
  if (antenna.enabled === false) reasons.push('Antenna disabled.');
  const bands = [mission.requirements.bandType, ...(mission.requirements.bandTypes ?? [])].filter(Boolean);
  if (bands.length && !bands.some((b) => antenna.capabilities.bands.includes(b!)))
    reasons.push('No compatible frequency band.');
  const look = pointingFor(antenna, mission, nowMs);
  if (!look) reasons.push('Orbital position unavailable: a usable TLE is required.');
  else {
    const min = antenna.coverage.elevationMinDeg ?? 5;
    if (look.elevationDeg < 0) reasons.push('Below the geometric horizon.');
    else if (look.elevationDeg < min)
      reasons.push(
        `Geometric elevation below ${min}° minimum; near-horizon refraction is applied by the contact engine.`,
      );
    if (look.elevationDeg > (antenna.coverage.elevationMaxDeg ?? 90))
      reasons.push('Above the antenna maximum elevation.');
    if (look.elevationDeg > 90 - (antenna.coverage.zenithExclusionDeg ?? 0))
      reasons.push('Inside the antenna zenith exclusion.');
    const mask = antenna.coverage.mask;
    if (mask?.length) {
      const delta = (az: number) => Math.abs(((az - look.azimuthDeg + 540) % 360) - 180);
      const nearest = mask.reduce((a, b) => (delta(a.az) <= delta(b.az) ? a : b));
      if (look.elevationDeg < nearest.minEl) reasons.push('Blocked by the configured terrain horizon mask.');
    }
  }
  const maintenance = antenna.capabilities.maintenanceWindowHrs ?? 0;
  const utcHour = (((nowMs % 86_400_000) + 86_400_000) % 86_400_000) / 3_600_000;
  if (maintenance > utcHour) reasons.push('Inside the configured maintenance window from 00:00 UTC.');
  if (
    results?.conflicts.some(
      (c) =>
        c.antennaId === antenna.id &&
        c.missionB === mission.id &&
        c.overlapStartMs <= nowMs &&
        nowMs < c.overlapEndMs,
    )
  )
    reasons.push('Planning allocation conflict: another mission has priority on this antenna.');
  return reasons;
}
